/**
 * Consent utility - Manages cookie consent preferences
 *
 * Separated from CookieConsent component to avoid circular dependencies
 * with analytics module.
 */

import { stringToBase64 } from "./base64";
import { logger } from "./logger";

const CONSENT_KEY = "cookie-consent";
const CONSENT_EXPIRY_DAYS = 365;

interface ConsentPreferences {
  accepted: boolean;
  timestamp: number;
  analytics: boolean;
  marketing: boolean;
}

/**
 * Get stored consent preferences with integrity validation
 */
export function getStoredConsent(): ConsentPreferences | null {
  try {
    const stored = localStorage.getItem(CONSENT_KEY);
    if (!stored) return null;

    const parsed = JSON.parse(stored);

    // Check if it's the new format with integrity
    if (parsed.data && parsed.integrity) {
      const data = JSON.stringify(parsed.data);
      const expectedIntegrity = stringToBase64(data).slice(0, 8);

      // Validate integrity
      if (parsed.integrity !== expectedIntegrity) {
        // Data may have been tampered with, clear it
        localStorage.removeItem(CONSENT_KEY);
        return null;
      }

      const consent = parsed.data as ConsentPreferences;
      const expiryDate = consent.timestamp + CONSENT_EXPIRY_DAYS * 24 * 60 * 60 * 1000;

      // Check if consent has expired
      if (Date.now() > expiryDate) {
        localStorage.removeItem(CONSENT_KEY);
        return null;
      }

      return consent;
    }

    // Legacy format (backward compatibility)
    const consent = parsed as ConsentPreferences;
    if (consent.timestamp) {
      const expiryDate = consent.timestamp + CONSENT_EXPIRY_DAYS * 24 * 60 * 60 * 1000;

      // Check if consent has expired
      if (Date.now() > expiryDate) {
        localStorage.removeItem(CONSENT_KEY);
        return null;
      }

      // Migrate to new format
      storeConsent(consent);
      return consent;
    }

    return null;
  } catch {
    return null;
  }
}

/**
 * Store consent preferences with integrity check
 */
export function storeConsent(preferences: ConsentPreferences): void {
  try {
    // Add integrity hash (simple checksum for tampering detection)
    const data = JSON.stringify(preferences);
    const integrity = stringToBase64(data).slice(0, 8); // Simple integrity check

    const stored = JSON.stringify({
      data: preferences,
      integrity,
      version: 1,
    });

    localStorage.setItem(CONSENT_KEY, stored);
  } catch (error) {
    logger.error("Failed to store consent preferences:", error);
  }
}

/**
 * Check if user has given consent
 */
export function hasConsent(): boolean {
  const consent = getStoredConsent();
  return consent?.accepted === true;
}

/**
 * Check if analytics consent is given
 */
export function hasAnalyticsConsent(): boolean {
  const consent = getStoredConsent();
  return consent?.analytics === true;
}

/**
 * Check if marketing consent is given
 */
export function hasMarketingConsent(): boolean {
  const consent = getStoredConsent();
  return consent?.marketing === true;
}

/**
 * Export consent preferences type for use in components
 */
export type { ConsentPreferences };
